import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import { AppContext } from '../../App';
import LabDataLayout, { useLabDataFilters } from '../../components/LabData/LabDataLayout';
import LabDataKpiCard from '../../components/LabData/LabDataKpiCard';
import { getBudgetDetail } from '../../clients/lab_data_client';
import { formatNisAmount, formatPercentUsed } from '../../utils/labData';
import { showToast } from '../../config_and_helpers/helpers';

const BreakdownTable = ({ title, rows, nameKey, amountKey }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {!rows?.length ? (
        <Typography color="text.secondary">No data.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Spend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={row.id ?? i}>
                <TableCell>{row[nameKey] ?? row.name ?? '—'}</TableCell>
                <TableCell align="right">
                  {formatNisAmount(row[amountKey] ?? row.total_nis ?? row.spent_nis)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

const BudgetDetailContent = () => {
  const { id } = useParams();
  const { token } = useContext(AppContext);
  const filters = useLabDataFilters();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!token || !id || !filters) return;
    setLoading(true);
    getBudgetDetail(token, id, { from: filters.from, to: filters.to }).then(res => {
      setLoading(false);
      if (!res.success) {
        showToast(res.messages?.[0] || 'Failed to load budget detail', 'error', 'top-right', 5000);
        return;
      }
      setDetail(res.data);
    });
  }, [token, id, filters]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const line = detail?.data?.budget_line ?? detail?.budget_line ?? detail?.data ?? {};
  const bySupplier = detail?.data?.by_supplier ?? detail?.by_supplier ?? [];
  const byProject = detail?.data?.by_project ?? detail?.by_project ?? [];
  const pct = Number(line.percent_used ?? 0);
  const numberLabel = line.number || line.code || '';
  const pi = line.primary_investigator;
  const piLabel = pi
    ? `${pi.first_name || ''} ${pi.last_name || ''}`.trim() || pi.username || '—'
    : '—';
  const relatedUsers = Array.isArray(line.related_users) ? line.related_users : [];

  return (
    <>
      <Typography component={Link} to="/lab-data/budgets" sx={{ display: 'inline-block', mb: 2 }}>
        ← Back to budgets
      </Typography>
      <Typography variant="h5" gutterBottom>
        {numberLabel}
        {line.name ? ` — ${line.name}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Primary investigator: {piLabel}
      </Typography>
      {relatedUsers.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Related users:{' '}
          {relatedUsers
            .map(
              u =>
                `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
                u.username ||
                `User ${u.id}`
            )
            .join(', ')}
        </Typography>
      ) : null}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <LabDataKpiCard label="Allocated" value={formatNisAmount(line.allocated_amount)} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <LabDataKpiCard label="Spent" value={formatNisAmount(line.spent_nis)} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <LabDataKpiCard label="Remaining" value={formatNisAmount(line.remaining_nis)} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <LabDataKpiCard label="Used" value={formatPercentUsed(pct)} />
        </Grid>
      </Grid>
      <Box sx={{ mb: 3, maxWidth: 480 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, pct))}
          sx={{ height: 10, borderRadius: 1 }}
        />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <BreakdownTable
            title="By supplier (top 10)"
            rows={bySupplier}
            nameKey="supplier_name"
            amountKey="spent_nis"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BreakdownTable
            title="By project (top 10)"
            rows={byProject}
            nameKey="project_name"
            amountKey="spent_nis"
          />
        </Grid>
      </Grid>
    </>
  );
};

const BudgetDetailPage = () => (
  <LabDataLayout title="Budget detail">
    <BudgetDetailContent />
  </LabDataLayout>
);

export default BudgetDetailPage;
